'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import {
  Check,
  Clapperboard,
  Download,
  Loader2,
  Play,
  RotateCcw,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useAuthToken } from '@/hooks/useAuthToken';
import { downloadVideoFile, downloadFromUrl } from '@/lib/downloadVideo';
import { useI18n } from '@/contexts/I18nContext';
import type { SubmittedInfo } from '@/components/studio/HeroGenerator';

interface JobDTO {
  jobId: string;
  status: 'created' | 'processing' | 'completed' | 'failed';
  outputs: string[];
  videoId?: string | null;
  error?: string;
}

/** Poll cadence. Matches the library grid so a job resolves at the same pace
 *  whichever screen the user happens to be watching. */
const POLL_MS = 3000;
/** How long a generation is assumed to take when drawing the progress bar.
 *  There is no server-side percentage — this is an ETA, and it deliberately
 *  stalls at 95% rather than pretending to finish before the job does. */
const ETA_MS = 45000;

export function StudioStage({
  jobId,
  info,
  onRegenerate,
  onDismiss,
  onPendingChange,
}: {
  jobId: string;
  info?: SubmittedInfo;
  onRegenerate: () => void;
  onDismiss: () => void;
  /** Mirrors the job's in-flight state so the composer above can show its
   *  own busy affordance instead of inviting a second submit. */
  onPendingChange?: (pending: boolean) => void;
}) {
  const { t } = useI18n();
  const { authedFetch, getToken } = useAuthToken();
  const [job, setJob] = useState<JobDTO | null>(null);
  const [progress, setProgress] = useState(0);
  const startedAt = useRef(Date.now());
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);

  const isVideo = info?.type !== 'image';
  const status = job?.status ?? 'created';
  const pending = status === 'created' || status === 'processing';
  const output = job?.outputs?.[0];

  // Reset per job — the same mounted stage is reused when the user generates
  // again, so elapsed time and playback state must not leak across jobs.
  useEffect(() => {
    startedAt.current = Date.now();
    setJob(null);
    setProgress(0);
    setDuration(null);
    setPlaying(false);
  }, [jobId]);

  useEffect(() => {
    onPendingChange?.(pending);
  }, [pending, onPendingChange]);

  const pollOnce = useCallback(async () => {
    try {
      const res = await authedFetch(`/api/ai/wavespeed/status/${jobId}`);
      const json = await res.json();
      if (json?.success) setJob(json.data as JobDTO);
    } catch {
      /* transient — the next tick retries */
    }
  }, [authedFetch, jobId]);

  useEffect(() => {
    if (!pending) return;
    pollOnce();
    const timer = setInterval(pollOnce, POLL_MS);
    return () => clearInterval(timer);
  }, [pending, pollOnce]);

  // Time-based ETA bar, capped below 100% until the job actually reports back.
  useEffect(() => {
    if (!pending) {
      setProgress(100);
      return;
    }
    const tick = () => {
      const elapsed = Date.now() - startedAt.current;
      setProgress(Math.min(95, Math.round((elapsed / ETA_MS) * 100)));
    };
    tick();
    const timer = setInterval(tick, 500);
    return () => clearInterval(timer);
  }, [pending, jobId]);

  const metaLine = [info?.modelLabel, info?.aspect, info?.duration, info?.resolution]
    .filter(Boolean)
    .join(' · ');

  const handleDownload = async () => {
    try {
      if (job?.videoId) await downloadVideoFile(job.videoId, getToken());
      else if (output) await downloadFromUrl(output, `cannect-${jobId}.${isVideo ? 'mp4' : 'png'}`);
    } catch {
      /* the helper already falls back to opening the file in a new tab */
    }
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const stageBase =
    'relative flex w-full items-center justify-center overflow-hidden rounded-[14px] border border-[var(--st-line)]';

  return (
    <div className="rounded-[20px] border border-[var(--st-line)] bg-[var(--st-panel)] p-[18px]">
      {status === 'failed' ? (
        <div className={`${stageBase} aspect-video flex-col gap-3 bg-[var(--st-stage)] px-6 text-center`}>
          <TriangleAlert className="h-9 w-9 text-[#F0324B]" />
          <p className="text-[15px] font-semibold text-[var(--st-text)]">
            {t('studio.stage.failedTitle')}
          </p>
          <p className="max-w-md text-[12.5px] font-medium text-[var(--st-muted)]">
            {job?.error || t('studio.generator.unknownError')}
          </p>
          <button
            type="button"
            onClick={onDismiss}
            className="mt-1 inline-flex h-9 items-center gap-1.5 rounded-xl border border-[var(--st-line)] px-3 text-[13px] font-semibold text-[var(--st-text)] transition hover:bg-[var(--st-chip)]"
          >
            <X className="h-[15px] w-[15px] text-[var(--st-muted)]" />
            {t('studio.stage.dismiss')}
          </button>
        </div>
      ) : pending ? (
        <>
          <div className={`${stageBase} aspect-video flex-col gap-3.5 bg-[var(--st-stage)]`}>
            <Loader2 className="h-10 w-10 animate-spin text-[var(--st-violet)]" />
            <p className="text-[15px] font-semibold text-[var(--st-text)]">
              {t(isVideo ? 'studio.stage.generatingVideo' : 'studio.stage.generatingImage')}
            </p>
            <p className="text-[12.5px] font-medium text-[var(--st-muted)]">
              {t('studio.stage.eta')}
            </p>
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--st-chip)]">
              <div
                className="h-full rounded-full bg-[var(--st-violet)] transition-[width] duration-500 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="truncate text-[12.5px] font-medium text-[var(--st-muted)]">
                {metaLine}
              </span>
              <span className="shrink-0 text-[12.5px] font-semibold text-[var(--st-text)]">
                {progress}%
              </span>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={`${stageBase} aspect-video bg-[var(--st-stage2)]`}>
            {isVideo ? (
              <video
                ref={videoRef}
                src={output}
                className="h-full w-full object-contain"
                playsInline
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                onEnded={() => setPlaying(false)}
                onClick={togglePlay}
              />
            ) : (
              <img src={output} alt="" className="h-full w-full object-contain" />
            )}

            {isVideo && !playing && (
              <button
                type="button"
                onClick={togglePlay}
                aria-label={t('studio.open')}
                className="absolute flex h-16 w-16 items-center justify-center rounded-full border border-white/[0.33] bg-white/[0.13] backdrop-blur-sm transition hover:bg-white/25"
              >
                <Play className="ml-0.5 h-[26px] w-[26px] text-white" />
              </button>
            )}

            <span className="absolute left-3 top-3 inline-flex h-[29px] items-center gap-1.5 rounded-full border border-[#1E5233] bg-[#13341F] px-3">
              <Check className="h-[13px] w-[13px] text-[#3BD07A]" />
              <span className="text-xs font-semibold text-[#7CE6A6]">
                {t('studio.stage.done')}
              </span>
            </span>

            {isVideo && duration != null && Number.isFinite(duration) && (
              <span className="absolute bottom-3 right-3 rounded-lg bg-black/[0.67] px-2 py-1 text-[11px] font-semibold text-white">
                {formatDuration(duration)}
              </span>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-[var(--st-text)]">
                {t(isVideo ? 'studio.stage.readyVideo' : 'studio.stage.readyImage')}
              </p>
              <p className="truncate text-xs font-medium text-[var(--st-muted)]">{metaLine}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onRegenerate}
                className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-[var(--st-line)] px-3 text-[13px] font-semibold text-[var(--st-text)] transition hover:bg-[var(--st-chip)]"
              >
                <RotateCcw className="h-[15px] w-[15px] text-[var(--st-muted)]" />
                {t('studio.stage.regenerate')}
              </button>
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-[var(--st-line)] bg-[var(--st-chip)] px-3 text-[13px] font-semibold text-[var(--st-text)] transition hover:bg-[var(--st-panel)]"
              >
                <Download className="h-[15px] w-[15px]" />
                {t('studio.download')}
              </button>
              {job?.videoId && (
                <Link
                  href={`/campaigns/new?videoId=${job.videoId}`}
                  className="inline-flex h-[38px] items-center gap-1.5 rounded-xl bg-[var(--st-violet)] px-3.5 text-[13px] font-semibold text-white transition hover:brightness-110"
                >
                  <Clapperboard className="h-[15px] w-[15px]" />
                  {t('studio.createCampaign')}
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
